import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import MagnifyingGlassIcon from '@src/svgs/MagnifyingGlassIcon';
import {
  buildSpeakerSegmentsFromElements,
  downsampleWaveform,
  getDurationFromElements,
} from '@src/utility/waveformUtils';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import type { VideoTimeRange } from './TranscriptVideoPlayer';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * PLAT-11877 — the canvas paints with the design system's tokens. A 2D context
 * cannot take `var(--x)`, so the values are read off the root once per frame
 * (cheap: a handful of property reads) and fall back to the token's documented
 * value where no stylesheet is loaded (tests).
 */
function readCanvasPalette() {
  const style =
    typeof window === 'undefined'
      ? null
      : window.getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) =>
    style?.getPropertyValue(name).trim() || fallback;
  return {
    accent: read('--accent', '#f2d093'),
    foreground: read('--foreground', '#e5e5e5'),
    textMuted: read('--text-muted', '#a3a3a3'),
    border: read('--border', '#525252'),
    rms: read('--surface-elevated', '#404040'),
  };
}

/** Minimum visible window in seconds when fully zoomed in */
const MIN_VISIBLE_SECONDS = 10;

/** Pick a round tick interval that yields ~5-8 ticks for the visible window */
const getTickInterval = (windowDuration: number): number => {
  const intervals = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
  const idealCount = 6;
  const ideal = windowDuration / idealCount;
  let best = intervals[0];
  for (const iv of intervals) {
    if (Math.abs(iv - ideal) <= Math.abs(best - ideal)) best = iv;
  }
  return best;
};

/**
 * Canvas-based waveform visualization with speaker coloring and zoom for the
 * Transcript & Speakers tab. Syncs with videoCreator.time via MobX
 * reaction (the TranscriptVideoPlayer's RAF loop drives that observable).
 *
 * Zoom: slider goes from 0 (full duration) to 1 (MIN_VISIBLE_SECONDS window).
 * The visible window is centered on the playhead and clamped to [0, duration].
 *
 * Performance: raw waveform samples (~7M) are pre-downsampled to 4000 buckets
 * once on load. At zoom=0 (no scroll), waveform bars are cached in an offscreen
 * canvas and only the playhead is redrawn per frame.
 */
const ChevronDownIcon = () => (
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
    <path d="M9 5.5L5 1.5L1 5.5" stroke="var(--accent)" strokeWidth="2" />
  </svg>
);

interface TranscriptSpeakersWaveformProps {
  zoomLevel: number;
  onZoomChange: (zoomLevel: number) => void;
  isWaveformVisible: boolean;
  onToggleWaveformVisible: () => void;
  /** Current playhead time, in seconds. Driven by the parent's videoCreator. */
  time: number;
  /** Called when the user clicks/drags the waveform to seek. */
  onSeek: (time: number) => void;
  /** Total duration of the source video, in seconds. */
  duration: number;
  /**
   * Original audio peak data (PLAT-10737), remapped onto the transcript-derived
   * `[0, duration]` timeline — the same clock the dub peaks map by. Rendered when
   * `showDubbed` is false. `null`/empty until the original waveform loads.
   */
  originalPeaks: number[] | null | undefined;
  /** Full story original transcript — source for speaker-colour segments. */
  originalTranscriptElements: TranscriptElement[] | undefined;
  /**
   * PLAT-10597 (DE6): the voice/language label shown after the time — e.g.
   * "Original Voice - English", "AI Voice - Japanese", "Original + AI Voice".
   * Present only when a translated language is active + dubbing is enabled;
   * omit to hide it (e.g. on the Original track). When `onToggleVoiceMode` is
   * given, clicking it cycles the mode (same state as the dropdown toggle).
   */
  voiceLabel?: string;
  /** True when a dub is part of what's playing (Dubbed/Both) — accent styling. */
  voiceLabelActive?: boolean;
  onToggleVoiceMode?: () => void;
  /**
   * PLAT-10620: dub peak data (0–255) spanning the full story timeline, derived
   * from the composed dub buffer. Rendered in place of the original peaks when
   * `showDubbed` is true. `null` while the dub track is still composing.
   */
  dubbedPeaks?: number[] | null;
  /** PLAT-10620: render the dubbed waveform (Dubbed mode) instead of original. */
  showDubbed?: boolean;
  /**
   * PLAT-10949: when the Highlights filter is in "show-only" mode, the player
   * plays only these time windows and skips the gaps. Passing them here dims
   * the non-playing regions of the waveform so what's shown matches what plays.
   * `null`/empty when no filter is active (whole waveform stays lit).
   */
  playbackSegments?: VideoTimeRange[] | null;
}

const TranscriptSpeakersWaveform: React.FC<TranscriptSpeakersWaveformProps> =
  observer(
    ({
      zoomLevel,
      onZoomChange,
      isWaveformVisible,
      onToggleWaveformVisible,
      time,
      onSeek,
      duration: durationProp,
      originalPeaks,
      originalTranscriptElements,
      voiceLabel,
      voiceLabelActive,
      onToggleVoiceMode,
      dubbedPeaks,
      showDubbed,
      playbackSegments,
    }) => {
      const canvasRef = useRef<HTMLCanvasElement>(null);
      const containerRef = useRef<HTMLDivElement>(null);
      const [isDragging, setIsDragging] = useState(false);
      const [localCurrentTime, setLocalCurrentTime] = useState(0);
      // Incremented by ResizeObserver to force a redraw even when localCurrentTime hasn't changed.
      // (setLocalCurrentTime(prev => prev) bails out in React 18 when the value is unchanged.)
      const [drawRevision, setDrawRevision] = useState(0);
      const preventFeedbackRef = useRef<number>(0);
      // Separate window center so click/drag seeks don't shift the view
      const windowCenterRef = useRef<number>(0);
      const lastContainerWidth = useRef(0);
      const lastContainerHeight = useRef(0);

      // Offscreen canvas for caching waveform bars (avoids redraw when only playhead moves)
      const offscreenRef = useRef<HTMLCanvasElement | null>(null);
      // Key to invalidate the offscreen cache
      const offscreenKeyRef = useRef<string>('');

      // Downsample the peak data for the active mode (runs once when it loads).
      // PLAT-10620/10737: both the dubbed and original peaks are pre-aligned to
      // the transcript `[0, duration]` clock (see computeDubPeaks /
      // computeOriginalPeaks), so this path is source-agnostic — it just picks
      // the array for the current mode. Empty until that array is ready.
      const downsampled = useMemo(() => {
        const peaks = showDubbed ? dubbedPeaks : originalPeaks;
        return peaks && peaks.length > 0 ? downsampleWaveform(peaks) : [];
      }, [originalPeaks, showDubbed, dubbedPeaks]);

      // Use mapping-aware color resolver so waveform colors match Speakers panel
      const { getSpeakerColor: speakerColorResolver } = useSpeakerLabels();

      // Build speaker segments from the full story original transcript
      const speakerSegments = useMemo(() => {
        if (!originalTranscriptElements) return [];
        return buildSpeakerSegmentsFromElements(
          originalTranscriptElements,
          speakerColorResolver,
        );
      }, [originalTranscriptElements, speakerColorResolver]);

      // PLAT-10737: trust the resolved full-story duration prop (the real video
      // length). Fall back to deriving from transcript elements only if the prop
      // isn't ready yet, so the timeline never collapses to 0 during load.
      const duration: number = useMemo(
        () =>
          durationProp > 0
            ? durationProp
            : getDurationFromElements(originalTranscriptElements, 0),
        [originalTranscriptElements, durationProp],
      );

      // Precompute global amplitude stats from the downsampled buckets
      const globalStats = useMemo(() => {
        if (downsampled.length === 0) {
          return { maxAmp: 1, minAmp: 0, maxRms: 1, minRms: 0 };
        }

        let maxAmp = 0;
        let minAmp = 255;
        let maxRms = 0;
        let minRms = 255;

        for (const bucket of downsampled) {
          if (bucket.max > maxAmp) maxAmp = bucket.max;
          if (bucket.rms > maxRms) maxRms = bucket.rms;
          if (
            bucket.max > 0 &&
            bucket.max < minAmp &&
            maxRms > 0 &&
            bucket.max > maxRms * 1.5
          ) {
            minAmp = bucket.max;
          }
          if (bucket.rms > 0 && bucket.rms < minRms) minRms = bucket.rms;
        }

        if (minAmp >= maxAmp) minAmp = 0;
        if (minRms >= maxRms) minRms = 0;

        return { maxAmp, minAmp, maxRms, minRms };
      }, [downsampled]);

      // Compute visible time window based on zoom level.
      // The window only scrolls when the playhead exits the visible range
      // (e.g. during playback). Click/drag seeks keep the window pinned.
      const visibleWindow = useMemo(() => {
        if (duration <= 0) return { start: 0, end: 0, windowDuration: 0 };

        const windowDuration =
          duration -
          zoomLevel * (duration - Math.min(MIN_VISIBLE_SECONDS, duration));

        let center = windowCenterRef.current;

        // If playhead is outside the current window, re-center on it
        const currentStart = center - windowDuration / 2;
        const currentEnd = center + windowDuration / 2;
        if (localCurrentTime < currentStart || localCurrentTime > currentEnd) {
          center = localCurrentTime;
          windowCenterRef.current = center;
        }

        let start = center - windowDuration / 2;
        let end = center + windowDuration / 2;

        if (start < 0) {
          start = 0;
          end = windowDuration;
        }
        if (end > duration) {
          end = duration;
          start = Math.max(0, end - windowDuration);
        }

        return { start, end, windowDuration };
      }, [duration, zoomLevel, localCurrentTime]);

      // Sync from time prop -> local playhead. External updates (playback,
      // transcript click) also update the window center so the view follows the
      // playhead. Click/drag on the waveform itself does NOT update the center
      // (preventFeedbackRef gates this).
      useEffect(() => {
        if (Date.now() - preventFeedbackRef.current < 100) return;
        windowCenterRef.current = time;
        setLocalCurrentTime(time);
      }, [time]);

      // Track container resizes (including CSS transitions) to redraw the waveform.
      // ResizeObserver fires for CSS height transitions (unlike window.resize).
      useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const { width, height } = container.getBoundingClientRect();
        lastContainerWidth.current = width;
        lastContainerHeight.current = height;

        const observer = new ResizeObserver(() => {
          const rect = container.getBoundingClientRect();
          if (
            rect.width !== lastContainerWidth.current ||
            rect.height !== lastContainerHeight.current
          ) {
            lastContainerWidth.current = rect.width;
            lastContainerHeight.current = rect.height;
            offscreenKeyRef.current = ''; // invalidate cache
            setDrawRevision(n => n + 1); // always increments → effect always re-runs
          }
        });

        observer.observe(container);
        return () => observer.disconnect();
      }, []);

      // Helper to get speaker color at a specific time (binary search)
      const getSpeakerColorAtTime = useCallback(
        (time: number): string => {
          if (speakerSegments.length === 0)
            return readCanvasPalette().textMuted;

          // Binary search: find the last segment where startTime <= time
          let lo = 0;
          let hi = speakerSegments.length - 1;
          let result = -1;

          while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            if (speakerSegments[mid].startTime <= time) {
              result = mid;
              lo = mid + 1;
            } else {
              hi = mid - 1;
            }
          }

          if (result >= 0 && time <= speakerSegments[result].endTime) {
            return speakerSegments[result].speakerColor;
          }
          return readCanvasPalette().textMuted;
        },
        [speakerSegments],
      );

      /**
       * Draw waveform bars onto a target canvas context.
       * Reads from pre-downsampled buckets instead of raw 7M samples.
       */
      const drawBars = useCallback(
        (
          ctx: CanvasRenderingContext2D,
          canvasWidth: number,
          canvasHeight: number,
          winStart: number,
          winEnd: number,
          windowDuration: number,
        ) => {
          // Tokens once per bar pass (see readCanvasPalette).
          const palette = readCanvasPalette();
          const dpr = window.devicePixelRatio;
          const targetBarWidthInPixels = 3 * dpr;
          const targetBarGapInPixels = 1 * dpr;
          const totalBarWidthWithGap =
            targetBarWidthInPixels + targetBarGapInPixels;
          const maxNumBars = Math.floor(canvasWidth / totalBarWidthWithGap);

          // Compute a stable waveStep from the zoom ratio
          const zoomRatio = windowDuration / duration;
          const totalBarsAtThisZoom = Math.floor(maxNumBars / zoomRatio);
          const waveStep = Math.max(
            1,
            Math.ceil(downsampled.length / totalBarsAtThisZoom),
          );

          // Snap the first bar to a global grid boundary
          const rawStartIdx = (winStart / duration) * downsampled.length;
          const gridAlignedStart =
            Math.floor(rawStartIdx / waveStep) * waveStep;

          const ampRange = globalStats.maxAmp - globalStats.minAmp;
          const rmsRange = globalStats.maxRms - globalStats.minRms;

          const rawEndIdx = Math.ceil((winEnd / duration) * downsampled.length);

          for (
            let dataPos = gridAlignedStart;
            dataPos < rawEndIdx;
            dataPos += waveStep
          ) {
            if (dataPos >= downsampled.length) break;
            const dataEnd = Math.min(dataPos + waveStep, downsampled.length);

            // Aggregate from pre-downsampled buckets (typically ~2-20 per bar, not ~37K)
            let maxAmplitude = 0;
            let sumRmsSq = 0;
            let count = 0;
            for (let j = dataPos; j < dataEnd; j++) {
              const b = downsampled[j];
              if (b.max > maxAmplitude) maxAmplitude = b.max;
              sumRmsSq += b.rms * b.rms;
              count++;
            }
            const rmsAmplitude = count > 0 ? Math.sqrt(sumRmsSq / count) : 0;

            const time = (dataPos / downsampled.length) * duration;
            const baseColor = getSpeakerColorAtTime(time);

            const barTimeFraction = (time - winStart) / windowDuration;
            const x = barTimeFraction * canvasWidth;

            if (x + targetBarWidthInPixels < 0 || x > canvasWidth) continue;

            const rawMaxValue =
              ampRange > 0 ? (maxAmplitude - globalStats.minAmp) / ampRange : 0;
            const rawRmsValue =
              rmsRange > 0 ? (rmsAmplitude - globalStats.minRms) / rmsRange : 0;

            const maxDrawHeight =
              Math.max(0, rawMaxValue) * canvasHeight * 0.99;
            const rmsDrawHeight =
              Math.max(0, rawRmsValue) * canvasHeight * 0.45;

            ctx.fillStyle = baseColor;
            ctx.fillRect(
              x,
              canvasHeight - maxDrawHeight,
              targetBarWidthInPixels,
              maxDrawHeight,
            );

            ctx.fillStyle = palette.rms;
            ctx.globalAlpha = 0.3;
            ctx.fillRect(
              x,
              canvasHeight - rmsDrawHeight,
              targetBarWidthInPixels,
              rmsDrawHeight,
            );
            ctx.globalAlpha = 1.0;
          }
        },
        [downsampled, duration, globalStats, getSpeakerColorAtTime],
      );

      // Track pending RAF retry so we can cancel on unmount / re-run
      const drawRetryRef = useRef<number>(0);

      // Draw the waveform on the canvas
      useEffect(() => {
        cancelAnimationFrame(drawRetryRef.current);

        const draw = () => {
          const canvas = canvasRef.current;
          if (!canvas || downsampled.length === 0 || duration <= 0) return;

          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          const rect = canvas.getBoundingClientRect();
          const dpr = window.devicePixelRatio;
          const w = Math.round(rect.width * dpr);
          const h = Math.round(rect.height * dpr);

          if (w === 0 || h === 0) {
            // Canvas just mounted and hasn't been laid out yet — retry next frame
            drawRetryRef.current = requestAnimationFrame(draw);
            return;
          }

          // Only resize canvas when dimensions actually change
          if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
            offscreenKeyRef.current = ''; // invalidate cache
          }

          const palette = readCanvasPalette();

          ctx.clearRect(0, 0, w, h);

          const {
            start: winStart,
            end: winEnd,
            windowDuration,
          } = visibleWindow;
          if (windowDuration <= 0) return;

          // --- Timecode ruler ---
          const RULER_HEIGHT = 18 * dpr;
          const tickInterval = getTickInterval(windowDuration);
          const firstTick = Math.ceil(winStart / tickInterval) * tickInterval;

          ctx.fillStyle = palette.textMuted;
          ctx.font = `${10 * dpr}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';

          for (let t = firstTick; t <= winEnd; t += tickInterval) {
            const frac = (t - winStart) / windowDuration;
            const x = frac * w;

            // Tick line
            ctx.fillStyle = palette.border;
            ctx.fillRect(x, RULER_HEIGHT - 4 * dpr, 1 * dpr, 4 * dpr);

            // Timecode label
            ctx.fillStyle = palette.textMuted;
            ctx.fillText(formatTime(t), x, 2 * dpr);
          }

          // --- Waveform bars (offset below ruler) ---
          const barsHeight = h - RULER_HEIGHT;

          // Build a cache key from everything that affects bar rendering.
          // PLAT-10620: include the mode — the original and dub both downsample
          // to the same bucket count, so without this the cached bars would
          // persist across an Original↔Dubbed switch (stale waveform).
          const cacheKey = `${w}:${barsHeight}:${winStart.toFixed(4)}:${winEnd.toFixed(4)}:${speakerSegments.length}:${downsampled.length}:${showDubbed ? 'dub' : 'orig'}`;

          if (offscreenKeyRef.current === cacheKey && offscreenRef.current) {
            // Cache hit: composite the cached bars
            ctx.drawImage(offscreenRef.current, 0, RULER_HEIGHT);
          } else {
            // Cache miss: draw bars and cache them
            if (!offscreenRef.current) {
              offscreenRef.current = document.createElement('canvas');
            }
            offscreenRef.current.width = w;
            offscreenRef.current.height = barsHeight;
            const offCtx = offscreenRef.current.getContext('2d');
            if (offCtx) {
              drawBars(offCtx, w, barsHeight, winStart, winEnd, windowDuration);
              offscreenKeyRef.current = cacheKey;
              ctx.drawImage(offscreenRef.current, 0, RULER_HEIGHT);
            }
          }

          // PLAT-10949: when a "show-only" highlight filter is active, dim the
          // regions the player skips so the waveform reflects what actually
          // plays. Shade the gaps between the (sorted, merged) playback windows
          // within the visible range; drawn after the bars but before the
          // playhead so the cursor stays crisp.
          if (playbackSegments && playbackSegments.length > 0) {
            const timeToX = (t: number) =>
              ((t - winStart) / windowDuration) * w;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            let cursor = winStart;
            for (const seg of playbackSegments) {
              if (seg.end <= winStart || seg.start >= winEnd) continue;
              if (seg.start > cursor) {
                const x0 = timeToX(cursor);
                const x1 = timeToX(seg.start);
                ctx.fillRect(x0, 0, x1 - x0, h);
              }
              cursor = Math.max(cursor, seg.end);
            }
            if (cursor < winEnd) {
              const x0 = timeToX(cursor);
              ctx.fillRect(x0, 0, w - x0, h);
            }
          }

          // Draw playhead (only thing that changes per frame during playback at zoom=0)
          const playheadFraction =
            windowDuration > 0
              ? (localCurrentTime - winStart) / windowDuration
              : 0;
          const position = playheadFraction * w;

          ctx.fillStyle = palette.foreground;
          ctx.fillRect(position - 1.5 * dpr, 0, 3 * dpr, h);

          ctx.shadowColor = palette.accent;
          ctx.shadowBlur = 5 * dpr;
          ctx.fillStyle = palette.accent;
          ctx.fillRect(position - 1 * dpr, 0, 2 * dpr, h);

          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
        };

        draw();

        return () => cancelAnimationFrame(drawRetryRef.current);
      }, [
        downsampled,
        localCurrentTime,
        drawRevision,
        duration,
        speakerSegments,
        visibleWindow,
        globalStats,
        drawBars,
        isWaveformVisible,
        playbackSegments,
      ]);

      const calculateTimeFromX = (clientX: number): number => {
        const el = canvasRef.current ?? containerRef.current;
        if (!el) return localCurrentTime;
        const rect = el.getBoundingClientRect();
        const pos = (clientX - rect.left) / rect.width;
        const { start: winStart, windowDuration } = visibleWindow;
        const time = winStart + pos * windowDuration;
        return Math.max(0, Math.min(time, duration));
      };

      const calculateTimeFromMousePosition = (
        e: React.MouseEvent<HTMLCanvasElement>,
      ): number => calculateTimeFromX(e.clientX);

      const handleTimeChange = (newTime: number) => {
        setLocalCurrentTime(newTime);
        preventFeedbackRef.current = Date.now();
        onSeek(newTime);
      };

      const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDragging(true);
        const time = calculateTimeFromMousePosition(e);
        handleTimeChange(time);
      };

      const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDragging) {
          const time = calculateTimeFromMousePosition(e);
          handleTimeChange(time);
        }
      };

      const handleMouseUp = () => setIsDragging(false);
      const handleMouseLeave = () => setIsDragging(false);

      const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onZoomChange(parseFloat(e.target.value));
      };

      // PLAT-10597 (DE6): clickable voice/language label rendered right after
      // the time (cycles Original → AI → Both). `data-no-seek` keeps a click
      // from seeking the waveform (the TopBar's click handler skips it).
      const voiceLabelEl = voiceLabel ? (
        <VoiceModeButton
          type="button"
          data-no-seek
          $active={!!voiceLabelActive}
          disabled={!onToggleVoiceMode}
          onClick={onToggleVoiceMode}
          title={
            onToggleVoiceMode
              ? 'Switch voice (Original / AI / Both)'
              : undefined
          }
        >
          {voiceLabel}
        </VoiceModeButton>
      ) : null;

      if (downsampled.length === 0 || duration <= 0) {
        return (
          <Container ref={containerRef}>
            <TopBar>
              <TimeDisplay>
                <CurrentTime>0:00</CurrentTime>
                {voiceLabelEl}
              </TimeDisplay>
              <ToggleGroup>
                <VisibilityToggle
                  onClick={onToggleWaveformVisible}
                  data-no-seek
                >
                  <VisibilityLabel>
                    {isWaveformVisible ? 'Hide Waveform' : 'Show Waveform'}
                  </VisibilityLabel>
                  <ToggleArrow $collapsed={!isWaveformVisible}>
                    <ChevronDownIcon />
                  </ToggleArrow>
                </VisibilityToggle>
              </ToggleGroup>
            </TopBar>
            {isWaveformVisible && (
              <LoadingMessage>
                {showDubbed
                  ? 'Loading dubbed waveform…'
                  : 'Loading waveform...'}
              </LoadingMessage>
            )}
          </Container>
        );
      }

      // Format visible window times for display
      const displayStart = zoomLevel > 0 ? visibleWindow.start : 0;
      const displayEnd = zoomLevel > 0 ? visibleWindow.end : duration;

      return (
        <Container ref={containerRef}>
          <TopBar
            onClick={(e: React.MouseEvent) => {
              // Don't seek when clicking on zoom controls or toggle
              if ((e.target as HTMLElement).closest('[data-no-seek]')) return;
              if (isWaveformVisible) {
                handleTimeChange(calculateTimeFromX(e.clientX));
              }
            }}
          >
            <TimeDisplay>
              <CurrentTime>{formatTime(localCurrentTime)}</CurrentTime>
              <TotalTime>
                {zoomLevel > 0
                  ? `${formatTime(displayStart)} – ${formatTime(displayEnd)}`
                  : formatTime(duration)}
              </TotalTime>
              {voiceLabelEl}
            </TimeDisplay>
            <ToggleGroup>
              {isWaveformVisible && (
                <ZoomControl data-no-seek>
                  <ZoomLabel>
                    {zoomLevel > 0
                      ? formatZoomLabel(visibleWindow.windowDuration)
                      : 'Full'}
                  </ZoomLabel>
                  <ZoomSlider
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={zoomLevel}
                    onChange={handleZoomChange}
                    title="Zoom: drag right to zoom in"
                  />
                  <ZoomIcon aria-hidden="true">
                    <MagnifyingGlassIcon size={12} />
                  </ZoomIcon>
                </ZoomControl>
              )}
              <VisibilityToggle onClick={onToggleWaveformVisible} data-no-seek>
                <VisibilityLabel>
                  {isWaveformVisible ? 'Hide Waveform' : 'Show Waveform'}
                </VisibilityLabel>
                <ToggleArrow $collapsed={!isWaveformVisible}>
                  <ChevronDownIcon />
                </ToggleArrow>
              </VisibilityToggle>
            </ToggleGroup>
          </TopBar>
          {isWaveformVisible && (
            <Canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              title="Click to seek to this position in the audio/video"
            />
          )}
        </Container>
      );
    },
  );

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format a duration into a human-readable zoom label.
 * Examples: "10s", "30s", "1m", "5m", "15m", "1h"
 */
const formatZoomLabel = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = seconds / 60;
    return m % 1 === 0 || m >= 10 ? `${Math.round(m)}m` : `${m.toFixed(1)}m`;
  }
  const h = seconds / 3600;
  return h % 1 === 0 || h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`;
};

/* PLAT-11877 — the same card as the tab's right column (design-system.md,
   "Transcript & Languages"): the panel surface with the review's radius, no
   rule, no inset vignette. */
const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: var(--surface);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  cursor: pointer;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: calc(100% - 30px);
  cursor: pointer;
`;

const TimeDisplay = styled.div`
  display: flex;
  gap: 12px;
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const CurrentTime = styled.span`
  color: var(--accent);
  font-weight: var(--font-weight-medium);
`;

const TotalTime = styled.span`
  color: var(--text-muted);
`;

// PLAT-10597 (DE6): the Original↔Dubbed voice label. Gold (--accent) in the
// dubbed state per the design system (visual/creative control); a subtle
// text button otherwise. Reverts to a plain label when not togglable.
const VoiceModeButton = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 0;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: ${p => (p.$active ? 'var(--accent)' : 'var(--text-secondary)')};
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover:not(:disabled) {
    color: var(--foreground);
  }
  &:disabled {
    cursor: default;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VisibilityLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const VisibilityToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--radius-input);
  transition: all var(--transition-normal);

  &:hover {
    background: var(--border);

    ${VisibilityLabel} {
      color: var(--text-secondary);
    }
  }
`;

const ToggleArrow = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  transform: ${props => (props.$collapsed ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ZoomLabel = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  min-width: 28px;
  text-align: right;
`;

const ZoomSlider = styled.input`
  width: 80px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--border);
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
    border: none;
  }
`;

const ZoomIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

export default TranscriptSpeakersWaveform;
